import SideNavLayout from "../customsidenav/sidenavlayout";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import dayjs from "dayjs";
import { Backdrop, CircularProgress } from "@mui/material";
import { ChevronLeft, ChevronRight, Plus, Search, Tag, User } from "lucide-react";
import ModalSwitch from "./modals/modalswitch";
import NewAppointments from "./modals/newappointment";
import api from "../api/api";
import isoWeek from "dayjs/plugin/isoWeek";
import { getPracticeId, practiceShortName } from "../config/practice";
import { CalendarPanel } from "../components/calendar/CalendarPanel";
import { DayView, LegendMenu, MonthView, WeekView, hourRange } from "./ScheduleViews";
import MultiFilter from "./MultiFilter";
import HourSheet from "./HourSheet";
import { categoryOf, isCancelled } from "./apptTypes";
import { canonicalRows, visibleRows } from "./scheduleRows";
import "./schedule.css";

const WEEKENDS_KEY = "bm.schedule.weekends";
dayjs.extend(isoWeek);

const VIEWS = [
  ["daily", "Day"],
  ["weekly", "Week"],
  ["monthly", "Month"],
];

const Schedule = () => {
  const [selectedDate, setSelectedDate] = React.useState(dayjs());
  const [isAptCard, setIsAptCard] = useState(false);
  const [Appointments, setNewAppointments] = useState(false);
  // Provider / visit-type filters: null shows all, else the picked names.
  const [selectedProviders, setSelectedProviders] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(null);
  const [scheduleData, setScheduleData] = React.useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentCardopen, setIsappointmentCardOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const practiseid = getPracticeId();
  const [providerOptions, setProviderOptions] = useState([]);
  const calendarRef = useRef(null);
  const suggestionref = useRef(null);
  const debounceTimer = useRef(null);
  const [screen, setScreen] = useState(0);
  const [isfutureappointment, setIsFutureAppointment] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  // The typed text, kept in state so the calendar can fade non-matches as you
  // type. The suggestions dropdown still comes from the debounced server call.
  const [searchTerm, setSearchTerm] = useState("");
  // The hour picked off the time gutter, drilled into below.
  const [hourFocus, setHourFocus] = useState(null);
  const [viewType, setViewType] = useState("daily"); // daily | weekly | monthly
  // Week and month views: Saturday and Sunday shown or not (remembered here).
  const [showWeekends, setShowWeekends] = useState(() => {
    try {
      return window.localStorage.getItem(WEEKENDS_KEY) !== "0";
    } catch (e) {
      return true;
    }
  });
  const toggleWeekends = () =>
    setShowWeekends((v) => {
      try {
        window.localStorage.setItem(WEEKENDS_KEY, v ? "0" : "1");
      } catch (e) {
        // not remembered; the switch still works
      }
      return !v;
    });
  // Where the clicked appointment sits, so its card opens beside it.
  const [anchor, setAnchor] = useState(null);
  // A click on empty grid time books there (provider, date and time prefilled).
  const [prefill, setPrefill] = useState(null);
  // Re-render each minute so the "now" line moves.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const isToday = selectedDate.isSame(dayjs(), "day");

  const getDateRange = () => {
    let start;
    let end = null;
    if (viewType === "daily") start = selectedDate.startOf("day");
    if (viewType === "weekly") {
      start = selectedDate.startOf("isoWeek");
      end = selectedDate.endOf("isoWeek");
    }
    // The month grid shows whole Mon-Sun weeks, so fetch those days too.
    if (viewType === "monthly") {
      start = selectedDate.startOf("month").startOf("isoWeek");
      end = selectedDate.endOf("month").endOf("isoWeek");
    }
    return {
      startdate: start.format("YYYY-MM-DD"),
      enddate: end ? end.format("YYYY-MM-DD") : undefined,
    };
  };

  // True when a search is running and this appointment is not one of the hits.
  // Local and instant - it does not wait for the debounced autocomplete.
  const dimOf = useCallback(
    (a) => {
      const q = searchTerm.trim().toLowerCase();
      if (!q) return false;
      const hay = [
        a?.firstname,
        a?.lastname,
        `${a?.firstname || ""} ${a?.lastname || ""}`,
        a?.patientName,
        a?.patientid != null ? String(a.patientid) : "",
        a?.purpose,
        a?.type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return !hay.includes(q);
    },
    [searchTerm],
  );

  // The text the latest debounced search was sent for: a reply for anything
  // else (the box was cleared or changed meanwhile) is dropped.
  const searchSeq = useRef("");
  const handleSearchChange = (e) => {
    const searchQuery = e.target.value.toLowerCase();
    setSearchTerm(e.target.value);
    searchSeq.current = searchQuery;
    clearTimeout(debounceTimer.current);
    if (searchQuery === "") {
      setFilteredSuggestions([]);
      return;
    }
    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await api.post(`/v1/${practiseid}/autocomplete/dssearch`, {
          searchtxt: searchQuery,
        });
        if (response.status == 200 && searchSeq.current === searchQuery) {
          setFilteredSuggestions(response?.data || []);
        }
      } catch (error) {
        console.error("Search API error:", error);
      }
    }, 1000);
  };

  // Only the newest request may write the grid: a slower reply for the view
  // or date just left (e.g. a week arriving after the day) is dropped.
  const fetchSeq = useRef(0);
  const fetchScheduleData = async () => {
    const seq = ++fetchSeq.current;
    setIsLoading(true);
    try {
      const { startdate, enddate } = getDateRange();
      const payload = { startdate, ...(enddate && { enddate }) };
      const response = await api.post(`/v1/${practiseid}/scheduler/schedule_list`, payload);
      if (seq !== fetchSeq.current) return;
      // schedule_list answers data "" when the range is empty
      if (response.status === 200 && Array.isArray(response.data)) {
        setScheduleData(response.data);
      } else {
        setScheduleData([]);
      }
    } catch (error) {
      console.error("Error fetching schedule data:", error);
    } finally {
      if (seq === fetchSeq.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${practiceShortName()} - Scheduler`;
    fetchScheduleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, viewType]);

  // The selected date carries over between views; Today jumps back.
  const handleViewChange = (type) => setViewType(type);

  const handleDateChange = (prop) => {
    setIsCalendarOpen(false);
    setSelectedDate(prop);
  };

  /* One provider, one column (formal spellings settle on the provider list's
     name by id; a deliberate reassignment keeps its linked provider), then the
     filters and one row per appointment: schedule/scheduleRows.js. */
  const scheduleRows = useMemo(() => canonicalRows(scheduleData, providerOptions), [scheduleData, providerOptions]);
  const filteredAppointments = useMemo(
    () => visibleRows(scheduleRows, selectedProviders, selectedTypes),
    [scheduleRows, selectedProviders, selectedTypes],
  );

  // Every provider: the practice's list plus anyone on the schedule it lacks.
  const providerList = useMemo(() => {
    const list = [...providerOptions];
    scheduleRows.forEach((a) => {
      if (!a.providername) return;
      const known =
        (a.providerid != null && list.some((p) => String(p.providerid) === String(a.providerid))) ||
        list.some((p) => p.providername === a.providername);
      // No provider-list row: the schedule's providerid is the Athena id, not
      // the list's row id the booking form matches on, so it is not carried -
      // a booking from this column leaves the provider for staff to pick.
      if (!known) list.push({ providerid: null, providername: a.providername });
    });
    return list;
  }, [providerOptions, scheduleRows]);
  // Columns / chips: the picked providers, or all of them.
  const allProviders = useMemo(
    () => (selectedProviders === null ? providerList : providerList.filter((p) => selectedProviders.includes(p.providername))),
    [providerList, selectedProviders],
  );

  // Filter options with their appointment counts in the period on screen
  // (cancelled / no-show not counted).
  const filterOptions = useMemo(() => {
    const rows = scheduleRows.filter((a) => !isCancelled(a));
    const countBy = (key) => rows.reduce((m, a) => ((m[a[key]] = (m[a[key]] || 0) + 1), m), {});
    const byProvider = countBy("providername");
    const byType = countBy("type");
    const types = [...new Set(scheduleRows.map((a) => a.type).filter(Boolean))].sort((x, y) => x.localeCompare(y));
    return {
      providers: providerList.map((p) => ({ value: p.providername, label: p.providername, count: byProvider[p.providername] || 0 })),
      types: types.map((t) => ({ value: t, label: t, count: byType[t] || 0, dot: categoryOf(t).color })),
    };
  }, [scheduleRows, providerList]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Bail-out updates: a fresh [] on every mousedown re-rendered the whole
      // page (and any open form) between mousedown and mouseup.
      if (calendarRef.current && !calendarRef.current.contains(e.target)) setIsCalendarOpen(false);
      if (suggestionref.current && !suggestionref.current.contains(e.target)) {
        setFilteredSuggestions((prev) => (prev.length ? [] : prev));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchProviderData = async () => {
    try {
      const response = await api.get(`/v1/${practiseid}/providers`);
      if (response.status === 200) {
        setProviderOptions(response.data);
      } else {
        setProviderOptions([]);
      }
    } catch (error) {
      console.error(error);
      setProviderOptions([]);
    }
  };

  useEffect(() => {
    fetchProviderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuggestionClick = async (appointment) => {
    const matches = (Array.isArray(scheduleData) ? scheduleData : []).filter(
      (app) => String(app.patientid) === String(appointment?.patientid),
    );
    setAnchor(null);
    if (matches?.length) {
      setIsFutureAppointment(false);
      setSelectedAppointment(matches[0]);
      setFilteredSuggestions([]);
      setScreen(1);
      setIsAptCard(true);
    } else {
      const appointmentsdata = await api.get(`/v1/${practiseid}/appointments/${appointment?.patientid}/future`);
      if (appointmentsdata.status == 200) {
        setIsFutureAppointment(true);
        setSelectedAppointment(appointmentsdata?.data[0] || {});
        setFilteredSuggestions([]);
        setScreen(0);
        setIsAptCard(true);
      }
    }
  };

  const step = (dir) => (e) => {
    e.stopPropagation();
    const unit = viewType === "weekly" ? "week" : viewType === "monthly" ? "month" : "day";
    setSelectedDate((prev) => prev.add(dir, unit));
  };

  const openDailyFromMonthly = (date) => {
    setViewType("daily");
    setSelectedDate(dayjs(date));
  };

  /* The sheet slices the hour itself - it also needs what was still running
     when the hour opened. The grid already scopes filteredAppointments to the
     view's own date range, so the day view gives that day and the week view
     that week. The bounds are the hours the grid drew, so the sheet's arrows
     cannot walk past them. */
  const [hourLo, hourHi] = useMemo(() => hourRange(filteredAppointments), [filteredAppointments]);

  const openAppointment = (appt, rect) => {
    // a grid block always opens on its card, as a scheduled (not future) visit
    setScreen(0);
    setIsFutureAppointment(false);
    setSelectedAppointment(appt);
    setAnchor(rect);
    setIsAptCard(true);
  };

  // Book on empty time; past days are not bookable (the form starts today).
  const bookAt = (provider, minutes, day = selectedDate) => {
    if (day.isBefore(dayjs(), "day")) return;
    setPrefill({
      providerid: provider.providerid,
      date: day.format("YYYY-MM-DD"),
      // plain arithmetic: startOf("day").add() is an hour off on DST change days
      time: `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`,
    });
    setNewAppointments(true);
  };

  const weekStart = selectedDate.startOf("isoWeek");
  const stepper = (() => {
    if (viewType === "weekly") {
      const end = weekStart.add(showWeekends ? 6 : 4, "day");
      const thisWeek = weekStart.isSame(dayjs().startOf("isoWeek"), "day");
      return {
        kicker: thisWeek ? "This week" : "Week",
        main: `${weekStart.format("MMM D")} – ${end.format(end.year() !== weekStart.year() ? "MMM D, YYYY" : weekStart.month() === end.month() ? "D, YYYY" : "MMM D, YYYY")}`,
      };
    }
    if (viewType === "monthly") {
      return {
        kicker: selectedDate.isSame(dayjs(), "month") ? "This month" : "Month",
        main: selectedDate.format("MMMM YYYY"),
      };
    }
    return { kicker: isToday ? "Today" : selectedDate.format("dddd"), main: selectedDate.format("MMMM D, YYYY") };
  })();

  const bookable = !selectedDate.isBefore(dayjs(), "day");

  return (
    <>
      <div className="h-full">
        <SideNavLayout>
          <div className="bmsc">
            {/* One toolbar: date and view on the left; filters, colour key,
                search and New appointment on the right. */}
            <div className="bmsc-bar">
              <div className="bmsc-step" ref={calendarRef}>
                <button type="button" className="bmsc-step-arrow" onClick={step(-1)} aria-label="Previous">
                  <ChevronLeft />
                </button>
                <button
                  type="button"
                  className="bmsc-step-mid"
                  onClick={() => setIsCalendarOpen((v) => !v)}
                  aria-expanded={isCalendarOpen}
                  title="Pick a date"
                >
                  <small>{stepper.kicker}</small>
                  <b>{stepper.main}</b>
                </button>
                <button type="button" className="bmsc-step-arrow" onClick={step(1)} aria-label="Next">
                  <ChevronRight />
                </button>
                {isCalendarOpen && (
                  <div className="bmsc-cal">
                    <div className="bmcal-quick">
                      {[
                        ["Today", "daily"],
                        ["This Week", "weekly"],
                        ["Month", "monthly"],
                      ].map(([label, view]) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => {
                            setViewType(view);
                            setSelectedDate(dayjs());
                            setIsCalendarOpen(false);
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <CalendarPanel value={selectedDate} onChange={handleDateChange} />
                  </div>
                )}
              </div>

              {/* Day view only: week and month have "This Week" / "Month" in
                  the date picker. */}
              {viewType === "daily" && (
                <button type="button" className="bmsc-btn" onClick={() => setSelectedDate(dayjs())}>
                  Today
                </button>
              )}

              <div className="bmsc-seg" role="tablist" aria-label="View">
                {VIEWS.map(([type, label]) => (
                  <button
                    key={type}
                    type="button"
                    role="tab"
                    aria-selected={viewType === type}
                    className={viewType === type ? "on" : ""}
                    onClick={() => handleViewChange(type)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {viewType !== "daily" && (
                <button
                  type="button"
                  className={`bmsc-wkend${showWeekends ? " on" : ""}`}
                  aria-pressed={showWeekends}
                  onClick={toggleWeekends}
                  title={showWeekends ? "Hide Saturday and Sunday" : "Show Saturday and Sunday"}
                >
                  <span className="bmsc-wkend-sw" aria-hidden="true" />
                  Weekends
                </button>
              )}

              <div className="bmsc-filters">
                <MultiFilter
                  icon={<User aria-hidden="true" />}
                  caption="Providers"
                  allLabel="All providers"
                  noun="providers"
                  options={filterOptions.providers}
                  selected={selectedProviders}
                  setSelected={setSelectedProviders}
                />
                <MultiFilter
                  icon={<Tag aria-hidden="true" />}
                  caption="Type"
                  allLabel="All types"
                  noun="types"
                  options={filterOptions.types}
                  selected={selectedTypes}
                  setSelected={setSelectedTypes}
                />
                <LegendMenu />
                <div className="bmsc-search" ref={suggestionref}>
                  <Search aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Find a patient"
                    aria-label="Find a patient on the schedule"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  {filteredSuggestions.length > 0 && (
                    <div className="bmsc-sugg" role="listbox">
                      {filteredSuggestions
                        .filter((value, index, self) => index === self.findIndex((t) => t.patientid === value.patientid))
                        .map((appointment) => (
                          <button
                            key={appointment.patientid}
                            type="button"
                            role="option"
                            aria-selected="false"
                            onClick={() => handleSuggestionClick(appointment)}
                          >
                            {appointment?.patientinfo}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="bmsc-new"
                  onClick={() => {
                    setPrefill(null);
                    setNewAppointments(true);
                  }}
                >
                  <Plus aria-hidden="true" />
                  New appointment
                </button>
              </div>
            </div>

            <div className={`bmsc-card ${viewType}`}>
              {viewType === "daily" && (
                <>
                  <DayView
                  dimOf={dimOf}
                    searchKey={searchTerm}
                    onPickHour={setHourFocus}
                    providers={allProviders}
                    appts={filteredAppointments}
                    onOpen={openAppointment}
                    onBook={bookable ? (p, m) => bookAt(p, m) : null}
                    isToday={isToday}
                  />
                </>
              )}
              {viewType === "weekly" && (
                <WeekView
                  dimOf={dimOf}
                  searchKey={searchTerm}
                  onPickHour={setHourFocus}
                  weekStart={weekStart}
                  weekends={showWeekends}
                  providers={allProviders}
                  appts={filteredAppointments}
                  onOpen={openAppointment}
                  onBook={(p, m, d) => bookAt(p, m, d)}
                  onPickDay={openDailyFromMonthly}
                />
              )}
              {viewType === "monthly" && (
                <MonthView
                  dimOf={dimOf}
                  month={selectedDate}
                  appts={filteredAppointments}
                  onPickDay={openDailyFromMonthly}
                  weekends={showWeekends}
                />
              )}
            </div>
          </div>
        </SideNavLayout>
        <Backdrop
          sx={{
            color: "secondary.main",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={isLoading}
        >
          <CircularProgress color="secondary" />
        </Backdrop>
      </div>

      <NewAppointments
        fetchScheduleData={fetchScheduleData}
        selectedAppointment={selectedAppointment}
        Appointments={Appointments}
        setNewAppointments={setNewAppointments}
        prefill={prefill}
      />
      <ModalSwitch
        isfutureappointment={isfutureappointment}
        setIsFutureAppointment={setIsFutureAppointment}
        setScreen={setScreen}
        screen={screen}
        selectedDate={selectedDate}
        fetchScheduleData={fetchScheduleData}
        appointmentCardopen={appointmentCardopen}
        setIsappointmentCardOpen={setIsappointmentCardOpen}
        isAptcard={isAptCard}
        setIsAptcard={setIsAptCard}
        selectedAppointment={selectedAppointment}
        anchor={anchor}
        lockCancelled
      />
      {hourFocus !== null && (
        <HourSheet
          hour={hourFocus}
          appts={filteredAppointments}
          onHour={setHourFocus}
          minHour={hourLo}
          maxHour={Math.max(hourLo, hourHi - 1)}
          dateLabel={
            viewType === "weekly"
              ? `${weekStart.format("MMM D")} – ${weekStart.add(showWeekends ? 6 : 4, "day").format("MMM D, YYYY")}`
              : selectedDate.format("dddd, MMMM D, YYYY")
          }
          dimOf={dimOf}
          onOpen={openAppointment}
          onClose={() => setHourFocus(null)}
        />
      )}
    </>
  );
};

export default Schedule;
